import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  FallingPetals,
  SparkleDust,
  VarmalaL1,
  VarmalaL2,
  VarmalaL3,
  VarmalaL4,
} from "@/components/VarmalaScroll.jsx";

/**
 * VarmalaVideo
 * -----------------------------------------------------------
 * Autoplaying varmala video that slows down under each text card so guests
 * can read. Tap anywhere to pause for a few seconds. No scroll pinning.
 */
const SRC = "/varmala/video/varmala.mp4";
const POSTER = "/varmala/video/poster.jpg";
const BACKDROP = "/varmala/video/backdrop.jpg";

const IS_PHONE =
  typeof window !== "undefined" && window.matchMedia("(max-width: 767px), (pointer: coarse)").matches;

const FADE_MS = 600;
const TAP_PAUSE_MS = 3000;
// the last card starts 1.5 s before the video ends; keep it this much longer
const END_HOLD_MS = 1500;
// The video plays straight through at normal speed; the cards take equal
// 3 s turns over it with 0.4 s gaps (video seconds). When it ends the
// names card returns and stays as the title card.
// (the video opens on its own last frame and dissolves in, so a replay is seamless;
// that adds 0.2 s to everything after it)
const SEGMENTS = [
  { from: 0, to: 3.0, rate: 1, layer: 1 },
  { from: 3.0, to: 3.4, rate: 1, layer: null },
  { from: 3.4, to: 6.2, rate: 1, layer: 2 },
  { from: 6.2, to: 6.6, rate: 1, layer: null },
  { from: 6.6, to: 11.6, rate: 1, layer: 3 }, // Shubh Muhurat gets 5 s to read the times
  { from: 11.6, to: 12.0, rate: 1, layer: null },
  { from: 12.0, to: null, rate: 1, layer: 4 }, // to the end, held END_HOLD_MS more, then the names return
];
const LAYERS = { 1: VarmalaL1, 2: VarmalaL2, 3: VarmalaL3, 4: VarmalaL4 };

const reducedMotion = () =>
  typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

export default function VarmalaVideo() {
  const sectionRef = useRef(null);
  const videoRef = useRef(null);
  const api = useRef({});
  const [layer, setLayer] = useState(() => (reducedMotion() ? 4 : 1));
  const [tapPaused, setTapPaused] = useState(false);
  const [needsTap, setNeedsTap] = useState(false);
  const [finished, setFinished] = useState(() => reducedMotion());

  useEffect(() => {
    const section = sectionRef.current;
    const video = videoRef.current;
    if (!section || !video) return;

    if (reducedMotion()) {
      const seekEnd = () => {
        if (Number.isFinite(video.duration)) video.currentTime = Math.max(0, video.duration - 0.05);
      };
      if (video.readyState >= 1) seekEnd();
      else video.addEventListener("loadedmetadata", seekEnd, { once: true });
      return () => video.removeEventListener("loadedmetadata", seekEnd);
    }

    // iOS and Safari only autoplay when these are set before the first play()
    video.muted = true;
    video.defaultMuted = true;
    video.setAttribute("muted", "");
    video.setAttribute("playsinline", "");

    let disposed = false;
    let started = false;
    let seg = 0;
    let phase = "idle"; // idle | play | done
    let cancelWatch = null;
    let held = false; // paused by tap
    let away = false; // paused because scrolled out of view
    let resumeId = null;
    let inView = false;
    let endId = null;
    let blocked = false; // autoplay refused (e.g. iOS Low Power Mode) until a tap

    // some browsers clamp or reject slow rates
    const setRate = (r) => {
      try {
        video.playbackRate = r;
      } catch {
        try {
          video.playbackRate = r < 1 ? 0.5 : r;
        } catch {
          /* keep the current rate */
        }
      }
    };

    const stopWatch = () => {
      cancelWatch?.();
      cancelWatch = null;
    };

    const enterSeg = (i) => {
      seg = i;
      setLayer(SEGMENTS[i].layer);
      setRate(SEGMENTS[i].rate);
    };

    // switch segments when the playhead reaches the end of the current one
    const watch = () => {
      stopWatch();
      if (SEGMENTS[seg].to == null) return;
      const check = (t) => {
        let moved = false;
        while (SEGMENTS[seg].to != null && t >= SEGMENTS[seg].to - 0.02) {
          enterSeg(seg + 1);
          moved = true;
        }
        return moved;
      };
      let id;
      if (video.requestVideoFrameCallback) {
        const tick = (_now, meta) => {
          check(meta?.mediaTime ?? video.currentTime);
          if (SEGMENTS[seg].to != null) id = video.requestVideoFrameCallback(tick);
        };
        id = video.requestVideoFrameCallback(tick);
        cancelWatch = () => video.cancelVideoFrameCallback(id);
      } else {
        const tick = () => {
          check(video.currentTime);
          if (SEGMENTS[seg].to != null) id = requestAnimationFrame(tick);
        };
        id = requestAnimationFrame(tick);
        cancelWatch = () => cancelAnimationFrame(id);
      }
    };

    const giveUp = () => {
      stopWatch();
      phase = "done";
      if (Number.isFinite(video.duration)) video.currentTime = Math.max(0, video.duration - 0.05);
      setLayer(4);
    };

    const playVideo = () => {
      if (phase !== "play" || held || away) return;
      setRate(SEGMENTS[seg].rate);
      const p = video.play();
      watch();
      p?.catch((err) => {
        if (disposed || err?.name === "AbortError") return;
        stopWatch();
        if (err?.name === "NotAllowedError") {
          blocked = true; // e.g. iOS Low Power Mode: any tap starts it
          setNeedsTap(true);
        }
        else giveUp();
      });
    };

    const pauseVideo = () => {
      stopWatch();
      video.pause();
    };

    const begin = () => {
      if (started) return;
      started = true;
      phase = "play";
      enterSeg(0);
      playVideo();
    };
    const maybeBegin = () => {
      if (inView && !document.body.classList.contains("locked")) begin();
    };

    const onEnded = () => {
      stopWatch();
      phase = "done";
      endId = setTimeout(() => {
        setLayer(1);
        setFinished(true);
      }, END_HOLD_MS);
    };

    const holdOn = () => {
      held = true;
      setTapPaused(true);
      if (phase === "play") pauseVideo();
      resumeId = setTimeout(holdOff, TAP_PAUSE_MS);
    };
    const holdOff = () => {
      clearTimeout(resumeId);
      resumeId = null;
      held = false;
      setTapPaused(false);
      playVideo();
    };

    const onTap = (e) => {
      if (phase !== "play") return;
      if (blocked) {
        blocked = false;
        setNeedsTap(false);
        playVideo();
        return;
      }
      if (e.target.closest("button, a")) return;
      if (held) holdOff();
      else holdOn();
    };

    // "Watch again" and the Home link: rewind and run the cards from the top.
    // If the section isn't on screen yet (Home is still scrolling up), wait
    // for the visibility observer to start it.
    const replay = () => {
      clearTimeout(endId);
      setFinished(false);
      clearTimeout(resumeId);
      resumeId = null;
      held = false;
      setTapPaused(false);
      setNeedsTap(false);
      pauseVideo();
      video.currentTime = 0;
      started = true;
      phase = "play";
      enterSeg(0);
      away = !inView;
      playVideo();
    };
    const onReplayEvent = () => replay();
    window.addEventListener("varmala:replay", onReplayEvent);

    api.current.tap = onTap;
    api.current.replay = replay;
    api.current.play = () => {
      blocked = false;
      setNeedsTap(false);
      playVideo();
    };

    video.addEventListener("ended", onEnded);
    const io = new IntersectionObserver(
      ([entry]) => {
        const r = entry.isIntersecting ? entry.intersectionRatio : 0;
        inView = r >= 0.5;
        maybeBegin();
        if (!started || phase !== "play") return;
        if (r < 0.2 && !away) {
          away = true;
          pauseVideo();
        } else if (r >= 0.5 && away) {
          away = false;
          playVideo();
        }
      },
      { threshold: [0, 0.2, 0.5, 1] }
    );
    io.observe(section);
    const mo = new MutationObserver(maybeBegin);
    mo.observe(document.body, { attributes: true, attributeFilter: ["class"] });

    return () => {
      disposed = true;
      clearTimeout(resumeId);
      clearTimeout(endId);
      stopWatch();
      video.pause();
      video.removeEventListener("ended", onEnded);
      window.removeEventListener("varmala:replay", onReplayEvent);
      io.disconnect();
      mo.disconnect();
    };
  }, []);

  const Layer = layer ? LAYERS[layer] : null;
  const ease = [0.22, 1, 0.36, 1];
  const blur = (px) => (IS_PHONE ? {} : { filter: `blur(${px}px)` });

  return (
    <section
      ref={sectionRef}
      id="varmala"
      onClick={(e) => api.current.tap?.(e)}
      className="relative w-full h-screen overflow-hidden bg-ink"
      // svh = the visible phone screen (100vh is taller than it on iOS)
      style={{ height: "100svh" }}
    >
      <img src={BACKDROP} alt="" className="absolute inset-0 w-full h-full object-cover" />
      <video
        ref={videoRef}
        src={SRC}
        poster={POSTER}
        muted
        playsInline
        preload="auto"
        className="absolute inset-0 w-full h-full object-cover md:object-contain"
        // soften the video behind a card so the text reads; sharp again between cards
        style={{
          filter: layer ? "blur(5px) brightness(0.78)" : "none",
          transform: "scale(1.03)", // hides the soft blurred edge
          transition: `filter ${FADE_MS}ms ease`,
        }}
      />

      {/* Overlays: vignette + bottom wash in wine tones */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: "radial-gradient(ellipse at center, rgba(42,8,20,0) 35%, rgba(42,8,20,0.55) 70%, rgba(30,6,16,0.9) 100%)" }}
      />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: "linear-gradient(to top, rgba(74,14,34,0.7), transparent 40%)" }}
      />

      <FallingPetals count={IS_PHONE ? 12 : 22} />
      <SparkleDust count={IS_PHONE ? 16 : 40} />

      <div className="absolute inset-0 pointer-events-none">
        <AnimatePresence>
          {Layer && (
            <motion.div
              key={layer}
              initial={{ opacity: 0, y: 30, ...blur(12) }}
              animate={{ opacity: 1, y: 0, ...blur(0) }}
              exit={{ opacity: 0, y: -30, ...blur(12) }}
              transition={{ duration: FADE_MS / 1000, ease }}
              className={`absolute inset-0 flex items-center justify-center px-4 ${
                layer === 4 || finished
                  ? "pointer-events-auto flex-col gap-4"
                  : "[&>div]:!bg-[rgba(74,14,34,0.3)] md:[&>div]:!bg-[rgba(74,14,34,0.2)]"
              }`}
            >
              <Layer />
              {finished && !reducedMotion() && (
                <button
                  type="button"
                  onClick={() => api.current.replay?.()}
                  className="font-body text-[11px] uppercase tracking-chip text-gold-light/90 hover:text-gold-light transition-colors"
                >
                  ↻ Watch again
                </button>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* once it has played, a quiet scroll hint at the very bottom edge */}
      <AnimatePresence>
        {finished && (
          <motion.button
            type="button"
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            onClick={() => {
              const next = document.getElementById("alliance");
              if (next) next.scrollIntoView({ behavior: "smooth" });
              else window.scrollBy({ top: window.innerHeight, behavior: "smooth" });
            }}
            aria-label="Scroll down"
            className="absolute inset-x-0 mx-auto w-max bottom-4 z-20 flex flex-col items-center gap-1 text-gold-light/85"
          >
            <span className="font-body text-[9px] uppercase tracking-chip -mr-[0.3em]">Scroll</span>
            <svg viewBox="0 0 24 24" className="w-5 h-5 animate-bounce" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
              <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </motion.button>
        )}
      </AnimatePresence>

      {/* autoplay blocked: a small round play button low on the screen; tapping anywhere also starts it */}
      {needsTap && (
        <button
          type="button"
          onClick={() => api.current.play?.()}
          aria-label="Play the varmala video"
          className="absolute left-1/2 -translate-x-1/2 bottom-[7%] z-30 flex flex-col items-center gap-2"
        >
          <span className="w-14 h-14 rounded-full bg-[rgba(74,14,34,0.55)] border border-gold/70 backdrop-blur-sm flex items-center justify-center shadow-[0_6px_18px_rgba(58,10,24,0.45)]">
            <svg viewBox="0 0 24 24" className="w-6 h-6 ml-1 fill-gold-light" aria-hidden="true">
              <path d="M7 4.5v15l12.5-7.5z" />
            </svg>
          </span>
          <span className="font-body text-[10px] uppercase tracking-chip text-gold-light/90">Tap to play</span>
        </button>
      )}

      <AnimatePresence>
        {tapPaused && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="absolute bottom-8 left-1/2 -translate-x-1/2 z-30 w-11 h-11 rounded-full bg-[rgba(74,14,34,0.6)] border border-gold/50 flex items-center justify-center gap-1 pointer-events-none"
            aria-hidden="true"
          >
            <span className="block w-1 h-4 rounded-sm bg-gold-light" />
            <span className="block w-1 h-4 rounded-sm bg-gold-light" />
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
